import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

export const getExercises = createAsyncThunk(
  "exercises/getAll",
  async ({ selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${apiBaseUrl}/exercises?account_id=${selectedAccount}`, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getExercise = createAsyncThunk(
  "exercises/getOne",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${apiBaseUrl}/exercises/${id}?account_id=${selectedAccount}`,
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createExercise = createAsyncThunk(
  "exercises/create",
  async ({ exercise, selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${apiBaseUrl}/exercises?account_id=${selectedAccount}`,
        { exercise },
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateExercise = createAsyncThunk(
  "exercises/update",
  async ({ id, exercise, selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${apiBaseUrl}/exercises/${id}?account_id=${selectedAccount}`,
        { exercise },
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getHemscapExercises = createAsyncThunk(
  "exercises/getHemscapExercises",
  async ({ selectedAccount, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ account_id: selectedAccount, ...filters });
      const res = await axios.get(`${apiBaseUrl}/hemscap/exercises?${params}`, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getServiceClasses = createAsyncThunk(
  "exercises/getServiceClasses",
  async ({ selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${apiBaseUrl}/exercises/service_classes?account_id=${selectedAccount}`, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteExercise = createAsyncThunk(
  "exercises/delete",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${apiBaseUrl}/exercises/${id}?account_id=${selectedAccount}`,
        { headers: authHeaders() }
      );
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const exerciseSlice = createSlice({
  name: "exercise",
  initialState: {
    exercisesList: [],
    currentExercise: null,
    hemscapExercises: [],
    serviceClasses: [],
    hemscapLoading: false,
    listLoading: false,  // for getExercises (list page)
    isLoading: false,    // for getExercise / create / update (single record)
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getExercises.pending,    (s) => { s.listLoading = true; s.error = null; })
      .addCase(getExercises.fulfilled,  (s, a) => { s.listLoading = false; s.exercisesList = a.payload?.data || a.payload || []; })
      .addCase(getExercises.rejected,   (s, a) => { s.listLoading = false; s.error = a.payload; })
      .addCase(getExercise.pending,     (s) => { s.isLoading = true; })
      .addCase(getExercise.fulfilled,   (s, a) => { s.isLoading = false; s.currentExercise = a.payload?.data || a.payload; })
      .addCase(getExercise.rejected,    (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(createExercise.fulfilled, (s, a) => {
        const ex = a.payload?.data || a.payload;
        s.exercisesList = [...s.exercisesList, ex];
      })
      .addCase(updateExercise.fulfilled, (s, a) => {
        const ex = a.payload?.data || a.payload;
        s.exercisesList = s.exercisesList.map((e) => e.id === ex.id ? ex : e);
        s.currentExercise = ex;
      })
      .addCase(deleteExercise.fulfilled, (s, a) => {
        s.exercisesList = s.exercisesList.filter((e) => e.id !== a.payload.id);
      })
      .addCase(getHemscapExercises.pending,    (s) => { s.hemscapLoading = true; })
      .addCase(getHemscapExercises.fulfilled,  (s, a) => {
        s.hemscapLoading = false;
        s.hemscapExercises = a.payload?.results ?? [];
      })
      .addCase(getHemscapExercises.rejected,   (s) => { s.hemscapLoading = false; })
      .addCase(getServiceClasses.fulfilled, (s, a) => {
        s.serviceClasses = a.payload?.data ?? a.payload ?? [];
      });
  },
});

export default exerciseSlice.reducer;
